export interface JwtPayload {
  exp?: number;
  scopes?: string[];
  remember_me?: boolean;
  [claim: string]: unknown;
}

export interface TokenDescription {
  redacted: string;
  payload: JwtPayload | null;
  scopes?: string[];
  expiresAt?: string;
  expiresInSeconds?: number;
}

const REDACT_HEAD = 6;
const REDACT_TAIL = 4;

/** Decodes the payload of a JWT without verifying it. Returns `null` for anything that is not a JWT. */
export function decodeJwtPayload(token: string): JwtPayload | null {
  const segments = token.split(".");
  if (segments.length !== 3) return null;
  try {
    const parsed: unknown = JSON.parse(atob(segments[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof parsed === "object" && parsed !== null ? (parsed as JwtPayload) : null;
  } catch {
    return null;
  }
}

/**
 * Shortens a token so two tokens can be told apart in a log, but it cannot be replayed:
 * the signature segment is never part of the result.
 */
export function redactToken(token: string): string {
  const unsigned = token.split(".").slice(0, 2).join(".");
  if (unsigned.length <= REDACT_HEAD + REDACT_TAIL + 8) return "[redacted]";
  return `${unsigned.slice(0, REDACT_HEAD)}…${unsigned.slice(-REDACT_TAIL)} (len ${token.length})`;
}

export function describeToken(token: string, now: number = Date.now()): TokenDescription {
  const payload = decodeJwtPayload(token);
  const description: TokenDescription = { redacted: redactToken(token), payload };
  if (payload === null) return description;
  if (Array.isArray(payload.scopes)) description.scopes = payload.scopes;
  if (typeof payload.exp === "number") {
    description.expiresAt = new Date(payload.exp * 1000).toISOString();
    description.expiresInSeconds = Math.round(payload.exp - now / 1000);
  }
  return description;
}

/** `true` only for a decodable token whose `exp` has passed; tokens without a readable `exp` are left to the server to judge. */
export function isTokenExpired(token: string, now: number = Date.now()): boolean {
  const payload = decodeJwtPayload(token);
  if (payload === null || typeof payload.exp !== "number") return false;
  return payload.exp * 1000 <= now;
}
